/* eslint-disable no-undef */
/* eslint-disable no-console */
import { getMainContractAddress, isSupportedNetwork, loadContracts } from "./contractsInteractions";
import { loadTrades, subscribeToPositionsManagerEvents } from "./positionsManagerInteractions";
import {
  allContractsLoaded,
  tradesLoaded,
  lpTradesLoaded,
  marketsLoaded,
  marketsSettled,
  liquidationTradesLoaded,
  insuranceFundLoaded,
  insurerBalanceLoaded
} from "../actions/actions.js";
import { loadLatestMarkets, periodicallyUpdateMarketBasePrices, subscribeToMarketFactoryEvents } from "./marketsInteractions";
import { loadExpirationTimes } from "./dateUtilsInteractions";
import { loadAllTokenPairs, loadPositionTokens, loadLPPositionTokens, updateUnderlyingMarketPrice, subscribeToTokenRegistryEvents } from "./tokensInteractions";
import { loadDepositAndWithdrawalEvents, loadSettleEvents, subscribeToUserBalancesEvents } from "./balancesInteractions";
import { loadMarginAccountData, loadMarginPoolData, loadMarginPoolEvents, subscribeToMarginPoolEvents } from "./marginInteractions";
import { batch } from "react-redux";
import {
  loadInsuranceFundBalances,
  loadInsuranceFundEvents,
  loadInsurerBalance,
  subscribeToInsuranceFundEvents,
  subscribeToInsuranceFundLossesCoveredEvents
} from "./insuranceFundInteractions";
import { addStores, setDBName } from "./interactionsManager.js";
import {
  ARBITRUM_SEPOLIA_FIRST_BLOCK,
  ARBITRUM_SEPOLIA_NETWORK_ID,
  BASE_SEPOLIA_FIRST_BLOCK,
  BASE_SEPOLIA_NETWORK_ID,
  BASE_MAINNET_NETWORK_ID,
  BASE_MAINNET_FIRST_BLOCK,
  SEPOLIA_FIRST_BLOCK,
  SEPOLIA_NETWORK_ID
} from "../../config.js";

export const init = async (web3, web3Read, web3ReadBackup, web3Events, web3Subs, networkId, userAddress, dispatch) => {
  console.log("Init called in rootInteractions. Main timer started.");
  let startTime = new Date();
  localStorage.setItem("ttl", Date.now());

  // check if on right network
  const supportedNetwork = await isSupportedNetwork(networkId, dispatch);
  if (!supportedNetwork) {
    return;
  }

  let web3ForRead = web3;
  let web3ForReadBackup = web3;
  const isEthereumSepolia = networkId == SEPOLIA_NETWORK_ID;
  const isArbitrumSepolia = networkId == ARBITRUM_SEPOLIA_NETWORK_ID;
  const isBaseSepolia = networkId == BASE_SEPOLIA_NETWORK_ID;
  const isBase = networkId == BASE_MAINNET_NETWORK_ID;

  if (isArbitrumSepolia || isEthereumSepolia || isBaseSepolia || isBase) {
    web3ForRead = web3Read;
    web3ForReadBackup = web3ReadBackup;
    console.log("Using main RPC for reading: ", web3Read.currentProvider.host);
    console.log("Using backup RPC for reading: ", web3ReadBackup.currentProvider.host);
  }

  const lastBlock = await web3ForRead.eth.getBlockNumber();
  let firstBlock = 0;
  if (isEthereumSepolia) {
    firstBlock = SEPOLIA_FIRST_BLOCK;
  } else if (isArbitrumSepolia) {
    firstBlock = ARBITRUM_SEPOLIA_FIRST_BLOCK;
  } else if (isBaseSepolia) {
    firstBlock = BASE_SEPOLIA_FIRST_BLOCK;
  } else if (isBase) {
    firstBlock = BASE_MAINNET_FIRST_BLOCK;
  }

  // once we have gammaContractsRegistry, we can use it's address as db name, so that we can cashe on multiple networks
  const dbName = getMainContractAddress(networkId);
  setDBName(dbName);
  await addStores([
    "MarketFactory-MarketCreated",
    "Settlements-Settlement",
    "LPManager-allEvents",
    "SettlementManager-allEvents",
    "PositionsManager-allEvents",
    "UserBalances-allEvents",
    "MarginPool-allEvents",
    "UserBalancesVault-allEvents",
    "InsuranceFund-allEvents",
    "GammaContractsRegistry-getMultiple"
  ]);

  const { contracts, contractsRead, contractsReadBackup } = await loadContracts(web3, web3ForRead, web3ForReadBackup, networkId, dispatch);

  const networkData = { firstBlock, lastBlock, contractsReadBackup };

  console.log("Contracts loaded in: " + (new Date() - startTime) + " ms");
  dispatch(allContractsLoaded(contracts));
  const { gammaContractsRegistry, marketFactory, dateUtils, insuranceFund } = contracts;

  // NOTE: use read loadHelper because it doesn't use events (for faster loading)
  const loadHelperRead = contractsRead.loadHelper;
  const settlementsRead = contractsRead.settlements;
  const positionsManagerRead = contractsRead.positionsManager;
  const lpManagerRead = contractsRead.lpManager;
  const settlementManagerRead = contractsRead.settlementManager;
  const marginPoolRead = contractsRead.marginPool;
  const userBalancesRead = contractsRead.userBalances;
  const userBalancesVaultRead = contractsRead.userBalancesVault;

  const gammaContractsRegistryAddress = localStorage.getItem("gammaContractsRegistryAddress");
  if (gammaContractsRegistryAddress) {
    if (gammaContractsRegistryAddress !== gammaContractsRegistry._address) {
      localStorage.setItem("gammaContractsRegistryAddress", gammaContractsRegistry._address);
      localStorage.setItem("notifications", "{}");
    }
  } else {
    localStorage.setItem("gammaContractsRegistryAddress", gammaContractsRegistry._address);
  }

  // load token pairs and get riskFreeRate
  const tokenPairs = await loadAllTokenPairs(web3ForRead, contractsRead, userAddress, dispatch);
  const riskFreeRate = tokenPairs[0].riskFreeRate / 100; // TODO: v1 this is a budzotina
  // todo: use price from loadLatestMarkets
  const underPriceBN = await updateUnderlyingMarketPrice(tokenPairs, loadHelperRead, dispatch); // returns avg underlying price

  const settlementsStream = await loadSettleEvents(networkData, settlementsRead, dispatch);
  const markets = await loadLatestMarkets(networkData, loadHelperRead, riskFreeRate, marketFactory, underPriceBN, settlementsStream, dispatch);
  const marketsImmutable = markets.map(market => market.immutableAttributes);
  const marketsMutable = markets.map(market => market.mutableAttributes);

  // load trades
  const trades = await loadTrades(
    networkData,
    positionsManagerRead,
    lpManagerRead,
    settlementManagerRead,
    userBalancesRead,
    userAddress,
    null,
    marketsImmutable
  );

  const { longShortTrades, lpTrades, liquidationTrades } = trades;

  // batch 1
  batch(() => {
    dispatch(marketsLoaded(marketsImmutable, marketsMutable));
    dispatch(tradesLoaded(longShortTrades));
    dispatch(lpTradesLoaded(lpTrades));
    dispatch(liquidationTradesLoaded(liquidationTrades));
    dispatch(marketsSettled(settlementsStream));
  });

  // batch 2 - these methods call dispatch, not optimal
  await Promise.all([
    loadPositionTokens(loadHelperRead, userBalancesRead, marketsImmutable, userAddress, dispatch),
    loadLPPositionTokens(networkData, web3ForRead, loadHelperRead, lpManagerRead, marketsImmutable, userBalancesRead, userAddress, underPriceBN, dispatch),
    loadMarginPoolEvents(networkData, marginPoolRead, userAddress, dispatch),
    loadMarginPoolData(loadHelperRead, marginPoolRead, underPriceBN, dispatch),
    loadMarginAccountData(userAddress, loadHelperRead, marginPoolRead, userBalancesRead, underPriceBN, dispatch),
    loadExpirationTimes(dateUtils, dispatch),
    loadDepositAndWithdrawalEvents(networkData, userBalancesVaultRead, userAddress, dispatch),
    loadInsuranceFundEvents(networkData, insuranceFund, userAddress, dispatch)
  ]);

  // batch 3
  const insuranceFundBalances = await loadInsuranceFundBalances(loadHelperRead);
  const insurerBalance = await loadInsurerBalance(loadHelperRead, userAddress);
  batch(() => {
    dispatch(insuranceFundLoaded(insuranceFundBalances));
    dispatch(insurerBalanceLoaded(insurerBalance));
  });

  // subscribe to all events
  subscribeToTokenRegistryEvents(web3ForRead, contractsRead, userAddress, dispatch);
  subscribeToPositionsManagerEvents(web3ForRead, contractsRead, riskFreeRate, userAddress, dispatch);
  subscribeToMarginPoolEvents(web3ForRead, contractsRead, underPriceBN, userAddress, dispatch);
  subscribeToMarketFactoryEvents(web3ForRead, contractsRead, riskFreeRate, userAddress, dispatch);
  subscribeToUserBalancesEvents(web3ForRead, contractsRead, networkData, riskFreeRate, userAddress, dispatch);
  subscribeToInsuranceFundEvents(web3ForRead, contractsRead, tokenPairs, userAddress, dispatch);
  subscribeToInsuranceFundLossesCoveredEvents(web3ForRead, contractsRead, userAddress, dispatch);

  // periodical price ticker
  periodicallyUpdateMarketBasePrices(loadHelperRead, userBalancesRead, marginPoolRead, userAddress, dispatch);
  console.log("Main timer: " + (new Date() - startTime) + " ms");
};

// todo: v2 not used anymore, delete later
// export const subscribeToNewBlocks = async (web3, exchangeRegistry, positionsManagerRead, userAccount, dispatch) => {
//   let subscription = web3.eth
//     .subscribe("newBlockHeaders", (error, result) => {
//       if (!error) {
//         console.log("New block");

//         // TODO: v2 for now, just subscribe to new block, and do nothing
//         // In production, this will be main method to sync with blockchain updates
//         // not 30 seconds fetchers

//         return;
//       }

//       console.error(error);
//     })
//     .on("connected", subscriptionId => {
//       //console.log("Connected:", subscriptionId);
//     })
//     .on("error", console.error);
// };
